import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { resolveImageUrl } from "@/lib/media";
import { motion } from "framer-motion";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.error("Failed to load gallery:", e);
        setItems([]);
      });
  }, []);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section-y bg-white"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">{t.gallery.overline}</div>
          <h2
            data-testid="gallery-title"
            className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
          >
            {t.gallery.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">{t.gallery.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {items.map((g, i) => (
            <motion.button
              key={g.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              onClick={() => setActive(g)}
              data-testid={`gallery-item-${g.id}`}
              className={`group relative overflow-hidden rounded-2xl bg-slate-100 ${
                i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"
              }`}
            >
              <img
                src={resolveImageUrl(g.image)}
                alt={lang === "id" ? g.caption_id : g.caption_en}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-blue-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white text-sm font-medium translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                {lang === "id" ? g.caption_id : g.caption_en}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[80] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <button
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox-close"
            aria-label="Close"
            className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-5xl w-full"
          >
            <img
              src={resolveImageUrl(active.image)}
              alt=""
              className="w-full max-h-[85vh] object-contain rounded-2xl"
            />
            <div className="text-center text-white/80 mt-4 text-sm">
              {lang === "id" ? active.caption_id : active.caption_en}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
